import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useCreateProject } from '@/hooks/use-projects';
import { createProjectSchema, type CreateProjectInput } from '@/lib/schemas';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

// Preset colors for project
const PRESET_COLORS = [
    '#6366f1', // Indigo
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#ef4444', // Red
    '#f97316', // Orange
    '#eab308', // Yellow
    '#22c55e', // Green
    '#14b8a6', // Teal
    '#06b6d4', // Cyan
    '#3b82f6', // Blue
];

// Preset emojis for project
const PRESET_EMOJIS = ['📁', '📋', '🚀', '💡', '🎯', '⭐', '🔥', '💻', '📊', '🎨'];

interface CreateProjectDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

type IconType = 'emoji' | 'image';

export function CreateProjectDialog({
    open,
    onOpenChange,
    onSuccess,
}: CreateProjectDialogProps) {
    const { t } = useI18n();
    const { toast } = useToast();
    const navigate = useNavigate();
    const createProject = useCreateProject();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
    const [selectedEmoji, setSelectedEmoji] = useState(PRESET_EMOJIS[0]);
    const [iconType, setIconType] = useState<IconType>('emoji');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<CreateProjectInput>({
        resolver: zodResolver(createProjectSchema),
        defaultValues: {
            name: '',
            description: '',
            color: PRESET_COLORS[0],
            iconEmoji: PRESET_EMOJIS[0],
        },
    });

    const projectName = watch('name');

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast({
                title: t('toast.invalidFileType'),
                description: t('projects.invalidFileType'),
                variant: 'destructive',
            });
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast({
                title: t('toast.fileTooLarge'),
                description: t('projects.fileTooLarge'),
                variant: 'destructive',
            });
            return;
        }

        setImageFile(file);
        setIconType('image');

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setIconType('emoji');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: CreateProjectInput) => {
        try {
            let imageUrl: string | null = null;

            // Convert image to base64 if selected
            if (iconType === 'image' && imageFile) {
                setIsUploading(true);
                try {
                    imageUrl = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            resolve(reader.result as string);
                        };
                        reader.onerror = () => {
                            reject(new Error('Failed to read file'));
                        };
                        reader.readAsDataURL(imageFile);
                    });
                } catch (error) {
                    toast({
                        title: t('toast.uploadFailed'),
                        description: t('createProject.uploadFailedDesc'),
                        variant: 'destructive',
                    });
                }
                setIsUploading(false);
            }

            const project = await createProject.mutateAsync({
                name: data.name,
                description: data.description,
                color: selectedColor as string,
                iconEmoji: iconType === 'emoji' ? (selectedEmoji || '📁') : '📁',
                imageUrl: imageUrl || undefined,
            });

            toast({
                title: t('createProject.success'),
                description: t('createProject.successDesc').replace('{name}', data.name),
            });

            handleClose(false);
            onSuccess?.();

            // Navigate to the created project
            navigate(`/projects/${project.id}`);
        } catch (error) {
            toast({
                title: t('common.error'),
                description:
                    error instanceof Error
                        ? error.message
                        : t('error.somethingWentWrong'),
                variant: 'destructive',
            });
        }
    };

    const handleClose = (newOpen: boolean) => {
        if (!newOpen) {
            reset();
            setSelectedColor(PRESET_COLORS[0]);
            setSelectedEmoji(PRESET_EMOJIS[0]);
            setIconType('emoji');
            setImageFile(null);
            setImagePreview(null);
        }
        onOpenChange(newOpen);
    };

    const isPending = createProject.isPending || isUploading;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogHeader>
                        <DialogTitle>{t('createProject.title')}</DialogTitle>
                        <DialogDescription>
                            {t('createProject.description')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        {/* Project name */}
                        <div className="grid gap-2">
                            <Label htmlFor="name">{t('createProject.projectName')}</Label>
                            <Input
                                id="name"
                                placeholder={t('createProject.projectNamePlaceholder')}
                                {...register('name')}
                                autoFocus
                            />
                            {errors.name && (
                                <p className="text-sm text-destructive">
                                    {errors.name.message}
                                </p>
                            )}
                        </div>

                        {/* Description */}
                        <div className="grid gap-2">
                            <Label htmlFor="description">
                                {t('createProject.projectDescription')}{' '}
                                <span className="text-foreground-muted">{t('createProject.descriptionOptional')}</span>
                            </Label>
                            <Textarea
                                id="description"
                                placeholder={t('createProject.projectDescriptionPlaceholder')}
                                rows={3}
                                {...register('description')}
                            />
                            {errors.description && (
                                <p className="text-sm text-destructive">
                                    {errors.description.message}
                                </p>
                            )}
                        </div>

                        {/* Icon Type Selector */}
                        <div className="grid gap-2">
                            <Label>{t('createProject.projectIcon')}</Label>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant={iconType === 'emoji' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setIconType('emoji')}
                                    className="flex-1"
                                >
                                    {t('createProject.emoji')}
                                </Button>
                                <Button
                                    type="button"
                                    variant={iconType === 'image' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setIconType('image')}
                                    className="flex-1"
                                >
                                    <ImageIcon className="h-4 w-4 mr-2" />
                                    {t('createProject.image')}
                                </Button>
                            </div>
                        </div>

                        {/* Icon emoji (shown when emoji type selected) */}
                        {iconType === 'emoji' && (
                            <div className="grid gap-2">
                                <Label>{t('createProject.chooseEmoji')}</Label>
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_EMOJIS.map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => setSelectedEmoji(emoji)}
                                            className={cn(
                                                "h-10 w-10 rounded-lg text-xl flex items-center justify-center transition-all",
                                                selectedEmoji === emoji
                                                    ? 'bg-primary/20 ring-2 ring-primary'
                                                    : 'bg-surface-hover hover:bg-surface-hover/80'
                                            )}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Image upload (shown when image type selected) */}
                        {iconType === 'image' && (
                            <div className="grid gap-2">
                                <Label>{t('createProject.uploadImage')}</Label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                {imagePreview ? (
                                    <div className="relative w-20 h-20">
                                        <img
                                            src={imagePreview}
                                            alt="Project icon preview"
                                            className="w-20 h-20 rounded-lg object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="h-20 w-20 rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center gap-1 transition-colors"
                                    >
                                        <Upload className="h-6 w-6 text-foreground-muted" />
                                        <span className="text-xs text-foreground-muted">{t('attachments.upload')}</span>
                                    </button>
                                )}
                                <p className="text-xs text-foreground-muted">
                                    {t('createProject.maxSizeHint')}
                                </p>
                            </div>
                        )}

                        {/* Color - only show when emoji type selected */}
                        {iconType === 'emoji' && (
                            <div className="grid gap-2">
                                <Label>{t('createProject.color')}</Label>
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setSelectedColor(color)}
                                            className={cn(
                                                "h-8 w-8 rounded-full transition-all",
                                                selectedColor === color
                                                    ? 'ring-2 ring-offset-2 ring-offset-surface ring-white'
                                                    : ''
                                            )}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Preview */}
                        <div className="grid gap-2">
                            <Label>{t('createProject.preview')}</Label>
                            <div className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg">
                                {iconType === 'image' && imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Project icon"
                                        className="h-10 w-10 rounded-lg object-cover"
                                    />
                                ) : (
                                    <div
                                        className="h-10 w-10 rounded-lg flex items-center justify-center text-xl"
                                        style={{ backgroundColor: selectedColor }}
                                    >
                                        {selectedEmoji}
                                    </div>
                                )}
                                <span className="font-medium text-foreground">
                                    {projectName || t('createProject.newProject')}
                                </span>
                            </div>
                        </div>

                        {/* Upload indicator */}
                        {isUploading && (
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm text-foreground-muted">{t('createProject.processingImage')}</span>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleClose(false)}
                            disabled={isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {t('createProject.createButton')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
